import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../basequery";

export const ROLES = Object.freeze({
  VIEW_ONLY: "ViewOnly",
  USER: "User",
  ADMIN: "Admin",
});
export type UserRole = (typeof ROLES)[keyof typeof ROLES];

export interface Me {
  oid: string;
  name: string;
  email?: string;
  preferred_username?: string;
  scopes: string[];
  roles: UserRole[];
}

export interface GetMeResponse {
  data: Me;
}

type RawMeResponse =
  | (Partial<Me> & { scp?: string })
  | { data?: Partial<Me> & { scp?: string } };

function normalizeMeResponse(response: RawMeResponse): Partial<GetMeResponse> {
  const rawUser =
    response && typeof response === "object" && "data" in response
      ? (response.data as Partial<Me> & { scp?: string })
      : (response as Partial<Me> & { scp?: string });

  const rawScopes = Array.isArray(rawUser?.scopes)
    ? rawUser.scopes
    : typeof rawUser?.scp === "string"
      ? rawUser.scp.split(" ").filter(Boolean)
      : [];

  const normalizedUser: Me = {
    oid: rawUser?.oid ?? "",
    name: rawUser?.name ?? "",
    email: rawUser?.email ?? rawUser?.preferred_username,
    preferred_username: rawUser?.preferred_username,
    scopes: rawScopes,
    roles: Array.isArray(rawUser?.roles) ? (rawUser.roles as UserRole[]) : [],
  };

  return { data: normalizedUser };
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseApiQuery,
  tagTypes: ["Me"],
  endpoints: (builder) => ({
    getMe: builder.query<Partial<GetMeResponse>, void>({
      query: () => ({ url: "users/me", method: "GET" }),
      transformResponse: (response: RawMeResponse) =>
        normalizeMeResponse(response),
      providesTags: ["Me"],
    }),
  }),
});

export const { useGetMeQuery, useLazyGetMeQuery } = userApi;
